// Add JS detection class as soon as possible
document.documentElement.classList.add('js');

class AccountDropdown {
  constructor() {
    this.dropdowns = document.querySelectorAll('[data-dropdown="account"]');
    this.headerHeight = document.querySelector('.header')?.offsetHeight || 60;
    this.isMobile = window.innerWidth <= 767;
    this.transitionDuration = 300; // Match your CSS transition duration
    
    this.init();
    this.bindEvents();
  }

  init() {
    // Set CSS variable for header height
    document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
    
    // Remove initial hide once JS is loaded
    document.documentElement.classList.add('js-loaded');
    
    // Initialize dropdowns
    this.dropdowns.forEach(wrapper => {
      const toggle = wrapper.querySelector('[data-dropdown-toggle]');
      const dropdown = wrapper.querySelector('.account-dropdown');
      
      // Create overlay for mobile
      if (!wrapper.querySelector('.dropdown-overlay')) {
        const overlay = document.createElement('div');
        overlay.className = 'dropdown-overlay';
        overlay.setAttribute('aria-hidden', 'true');
        wrapper.appendChild(overlay);
      }
      
      // Set initial ARIA states
      if (toggle && dropdown) {
        toggle.setAttribute('aria-expanded', 'false');
        dropdown.setAttribute('aria-hidden', 'true');
        
        // Handle desktop hover if not mobile
        if (!this.isMobile) {
          this.setupDesktopHover(wrapper, toggle, dropdown);
        }
      }
    });
  }

  bindEvents() {
    // Toggle dropdown on click
    this.dropdowns.forEach(wrapper => {
      const toggle = wrapper.querySelector('[data-dropdown-toggle]');
      const overlay = wrapper.querySelector('.dropdown-overlay');
      
      if (toggle) {
        toggle.addEventListener('click', (e) => this.handleToggle(e, wrapper, toggle));
      }

      // Handle overlay click
      if (overlay) {
        overlay.addEventListener('click', () => this.closeDropdown(wrapper));
      }
    });

    // Close when clicking outside
    document.addEventListener('click', (e) => this.handleOutsideClick(e));
    
    // Close on escape key
    document.addEventListener('keydown', (e) => this.handleEscapeKey(e));
    
    // Update on resize
    window.addEventListener('resize', this.debounce(() => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 767;
      this.headerHeight = document.querySelector('.header')?.offsetHeight || 60;
      document.documentElement.style.setProperty('--header-height', `${this.headerHeight}px`);
      
      // Reinitialize hover events if switching between mobile and desktop
      if (wasMobile !== this.isMobile) {
        this.dropdowns.forEach(wrapper => {
          const toggle = wrapper.querySelector('[data-dropdown-toggle]');
          const dropdown = wrapper.querySelector('.account-dropdown');
          
          if (!this.isMobile && toggle && dropdown) {
            this.setupDesktopHover(wrapper, toggle, dropdown);
          }
        });
      }
    }, 250));
  }

  setupDesktopHover(wrapper, toggle, dropdown) {
    let hoverTimeout;
    
    wrapper.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimeout);
      this.openDropdown(wrapper);
    });
    
    wrapper.addEventListener('mouseleave', () => {
      hoverTimeout = setTimeout(() => {
        this.closeDropdown(wrapper);
      }, 150); // Small delay to prevent accidental closing
    });
    
    // Prevent immediate close when moving to dropdown
    dropdown.addEventListener('mouseenter', () => {
      clearTimeout(hoverTimeout);
    });
  }

  handleToggle(e, wrapper, toggle) {
    // Only prevent default on mobile
    if (this.isMobile) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    const isOpen = wrapper.getAttribute('data-dropdown-open') === 'true';
    
    // Close all other dropdowns
    this.dropdowns.forEach(other => {
      if (other !== wrapper) {
        this.closeDropdown(other);
      }
    });
    
    // Toggle current dropdown
    if (isOpen) {
      this.closeDropdown(wrapper);
    } else {
      this.openDropdown(wrapper);
    }
  }

  openDropdown(wrapper) {
    const toggle = wrapper.querySelector('[data-dropdown-toggle]');
    const dropdown = wrapper.querySelector('.account-dropdown');
    const overlay = wrapper.querySelector('.dropdown-overlay');
    const dropdownId = dropdown?.id;
    
    wrapper.setAttribute('data-dropdown-open', 'true');
    toggle?.setAttribute('aria-expanded', 'true');
    
    if (dropdown) {
      dropdown.setAttribute('aria-hidden', 'false');
      // Update focus management
      if (dropdownId) {
        toggle?.setAttribute('aria-controls', dropdownId);
      }
    }
    
    if (this.isMobile) {
      document.body.style.overflow = 'hidden';
      if (overlay) {
        overlay.style.display = 'block';
        // Delay setting opacity to allow transition
        requestAnimationFrame(() => {
          overlay.style.opacity = '1';
        });
      }
    }
  }

  closeDropdown(wrapper) {
    const toggle = wrapper.querySelector('[data-dropdown-toggle]');
    const dropdown = wrapper.querySelector('.account-dropdown');
    const overlay = wrapper.querySelector('.dropdown-overlay');
    
    wrapper.setAttribute('data-dropdown-open', 'false');
    toggle?.setAttribute('aria-expanded', 'false');
    dropdown?.setAttribute('aria-hidden', 'true');
    
    if (this.isMobile) {
      document.body.style.overflow = '';
      if (overlay) {
        overlay.style.opacity = '0';
        // Remove display after transition
        setTimeout(() => {
          overlay.style.display = 'none';
        }, this.transitionDuration);
      }
    }
  }

  handleOutsideClick(e) {
    if (this.isMobile) {
      // On mobile, we use the overlay for outside clicks
      return;
    }
    
    const clickedDropdown = e.target.closest('[data-dropdown="account"]');
    if (!clickedDropdown) {
      this.dropdowns.forEach(dropdown => {
        this.closeDropdown(dropdown);
      });
    }
  }

  handleEscapeKey(e) {
    if (e.key === 'Escape') {
      this.dropdowns.forEach(dropdown => {
        this.closeDropdown(dropdown);
        // Focus the toggle button after closing
        const toggle = dropdown.querySelector('[data-dropdown-toggle]');
        toggle?.focus();
      });
    }
  }

  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Initialize on DOM load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new AccountDropdown();
  });
} else {
  // DOM already loaded
  new AccountDropdown();
}